import produce from "immer";
import { memoize } from "lodash";
import { get as getPathValue } from "lodash";
import type {
  BaseBlock,
  ChildrenProp,
} from "@circle-react-shared/BuilderUI/DataModel.types";
import { cloneWithNewIDs } from "@circle-react-shared/BuilderUI/Helpers/cloneWithNewIDs";
import {
  isBaseBlock,
  isBlockArray,
  isChildrenProp,
} from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { ComponentMeta } from "../../BuilderUI.types";
import { getBlockPathInfo, getParentArray } from "../../Helpers/blockPath";
import type { ListPolicies } from "../types";

export const getRelativePath = memoize((path: string) =>
  path.replace(/^blocks\./, ""),
);

export const getParentArrayPath = memoize((path: string) =>
  path.replace(/\.\d+$/, ""),
);

export const batchUpdates = (updates: Array<() => void>) => {
  requestAnimationFrame(() => {
    updates.forEach(update => update());
  });
};

export const resolveInsertIndex = ({
  blocks,
  path,
  block,
  index,
  listPolicies,
}: {
  blocks: BaseBlock;
  path: string;
  block: BaseBlock | BaseBlock[];
  index: number | undefined;
  listPolicies: ListPolicies | undefined;
}): number => {
  const baseChildrenProp: unknown = getPathValue(blocks, path);
  if (!isChildrenProp(baseChildrenProp)) return index ?? 0;
  const childrenProp: ChildrenProp = baseChildrenProp;

  const policy = listPolicies?.[`${path}.value`];
  const defaultInsertionIndex = policy?.getDefaultInsertionIndex
    ? policy.getDefaultInsertionIndex({
        parentArray: childrenProp.value,
        parentPath: path,
        block,
      })
    : childrenProp.value.length;

  return index ?? defaultInsertionIndex;
};

export const insertClonedBlocks = ({
  childrenProp,
  insertIndex,
  block,
}: {
  childrenProp: ChildrenProp;
  insertIndex: number;
  block: BaseBlock | BaseBlock[];
}): void => {
  if (Array.isArray(block)) {
    const clonedBlocks = new Array(block.length);
    for (let i = 0; i < block.length; i++) {
      clonedBlocks[i] = cloneWithNewIDs(block[i]);
    }
    childrenProp.value.splice(insertIndex, 0, ...(clonedBlocks as BaseBlock[]));
    return;
  }
  childrenProp.value.splice(insertIndex, 0, cloneWithNewIDs(block));
};

export const findBlockById = (
  node: unknown,
  blockId: string,
): BaseBlock | null => {
  if (isBaseBlock(node) && node.id === blockId) return node;
  if (!node || typeof node !== "object") return null;
  for (const value of Object.values(node)) {
    const found = findBlockById(value, blockId);
    if (found) return found;
  }

  return null;
};

export const enrichBlockWithMeta = (
  block: BaseBlock | null,
  metaMapping: Record<string, ComponentMeta>,
): { block: BaseBlock; meta: ComponentMeta } | null => {
  if (!block) return null;
  const meta = metaMapping[block.type];
  return meta ? { block, meta } : null;
};

export const findBlockPathById = (
  blocks: BaseBlock | BaseBlock[],
  blockId: string,
  currentPath = "blocks",
): string | null => {
  if (Array.isArray(blocks)) {
    return findInArray(blocks, blockId, currentPath);
  }

  if (blocks?.id === blockId) {
    return currentPath;
  }

  if (!blocks?.props) {
    return null;
  }

  return findInProps(blocks.props, blockId, currentPath);
};

export const canMoveBlockInDirection = (
  blockPath: string,
  direction: "up" | "down",
  blocks: BaseBlock,
  listPolicies?: ListPolicies,
): boolean => {
  const { currentIndex, relativeParentPath } = getBlockPathInfo(blockPath);
  if (currentIndex === -1) return false;

  const parentArray = getParentArray(blocks, relativeParentPath);
  if (!parentArray) return false;

  const toIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

  const policy = listPolicies?.[relativeParentPath];
  if (policy?.canMoveChild) {
    return policy.canMoveChild({
      parentArray,
      parentPath: relativeParentPath,
      fromIndex: currentIndex,
      toIndex,
      child: parentArray[currentIndex],
      direction,
    });
  }

  return direction === "up"
    ? currentIndex > 0
    : currentIndex < parentArray.length - 1;
};

export const duplicateBlockAtPath = (
  blocks: BaseBlock,
  blockPath: string,
): { newBlocks: BaseBlock; newBlockPath: string } | null => {
  const { currentIndex, relativeParentPath } = getBlockPathInfo(blockPath);
  if (currentIndex === -1) return null;

  const newBlocks = produce(blocks, draft => {
    const parentArray: unknown = getPathValue(draft, relativeParentPath);
    if (!isBlockArray(parentArray)) return;

    const selectedBlock = parentArray[currentIndex];
    if (!isBaseBlock(selectedBlock)) return;

    const duplicated = cloneWithNewIDs(selectedBlock);
    parentArray.splice(currentIndex + 1, 0, duplicated);
  });

  const parentArrayPath = blockPath.replace(/\.\d+$/, "");
  const newBlockPath = `${parentArrayPath}.${currentIndex + 1}`;

  return { newBlocks, newBlockPath };
};

const findInArray = (
  blocks: BaseBlock[],
  blockId: string,
  currentPath: string,
): string | null => {
  for (let i = 0; i < blocks.length; i++) {
    const result = findBlockPathById(blocks[i], blockId, `${currentPath}.${i}`);
    if (result) return result;
  }
  return null;
};

const findInProps = (
  props: Record<string, any>,
  blockId: string,
  currentPath: string,
): string | null => {
  for (const [propName, propValue] of Object.entries(props)) {
    const value = propValue?.value;

    if (!canContainBlocks(value)) continue;

    const result = findBlockPathById(
      value,
      blockId,
      `${currentPath}.props.${propName}.value`,
    );
    if (result) return result;
  }
  return null;
};

const canContainBlocks = (value: any): boolean =>
  value && (isBaseBlock(value) || Array.isArray(value));

export const updateBlockAtPath = (
  blocks: BaseBlock,
  blockPath: string,
  updateFn: (block: BaseBlock) => void,
) => {
  const relativePath = getRelativePath(blockPath);
  return produce(blocks, draft => {
    const block = getPathValue(draft, relativePath) as BaseBlock;
    if (block) updateFn(block);
  });
};
